import React from 'react';
import PropTypes from 'prop-types';
import { Keyboard } from '../../../utils/Keyboard';
import {
  getTimePeriod,
  transform28HoursTo24Hours,
  sort28HoursFormat,
} from '../../../utils/Humanizers';

const bem = 'tt-schedule';

const scheduleClassName = `${bem}__row`;
const scheduleClassNameWithHourChange = `${bem}__row ${bem}__row--hourchange`;

// Given a schedule raw data from server, transform it to an easy to display format
export const generateDisplaySchedule = (schedule, searchMode) => schedule.sort(sort28HoursFormat).map((time, index, ary) => {
  // computing AM PM markers
  const currentAM = getTimePeriod(time);
  const prevAM = index >= 1 ? getTimePeriod(ary[index - 1]) : currentAM;
  let am = '';
  const changeAM = index === 0 || currentAM !== prevAM;
  if (changeAM) am = currentAM;


  // computing hour segments
  const hourChange = index === 1
    || index === ary.length - 1
    || changeAM
    || ary[index].split(':')[0] !== ary[index - 1].split(':')[0];

  // computing bus marker
  let busMarker = '';
  const showBusMarker = ary.length > 1;
  if (showBusMarker && index === 0) busMarker = `First ${searchMode.trim().toLowerCase()}`;
  else if (showBusMarker && index === ary.length - 1) busMarker = `Last ${searchMode.trim().toLowerCase()}`;

  return {
    key: index, am, busMarker, time: transform28HoursTo24Hours(time), hourChange,
  };
});

// eslint-disable-next-line no-param-reassign
const disableFocus = refs => refs.forEach((ref) => { ref.current.tabIndex = '-1'; });
// eslint-disable-next-line no-param-reassign
const enableFocus = refs => refs.forEach((ref) => { ref.current.tabIndex = '0'; });


export default class Schedule extends React.PureComponent {
  constructor(props) {
    super(props);
    this.liveMessageRef = React.createRef();
    this.scheduleRefs = [];
    this.curRefIndex = 0;
  }

  traverseSchedule = (ev) => {
    let nextIndex;
    switch (ev.keyCode) {
      case Keyboard.ARROW_DOWN:
        if (this.scheduleRefs) {
          nextIndex = (this.curRefIndex >= this.scheduleRefs.length - 1) ? 0 : this.curRefIndex + 1;
          this.scheduleRefs[nextIndex].current.focus();
          ev.stopPropagation();
          ev.preventDefault();
        }
        break;
      case Keyboard.ARROW_UP:
        if (this.scheduleRefs) {
          nextIndex = (this.curRefIndex <= 0) ? this.scheduleRefs.length - 1 : this.curRefIndex - 1;
          this.scheduleRefs[nextIndex].current.focus();
          ev.stopPropagation();
          ev.preventDefault();
        }
        break;
      case Keyboard.TAB:
        // due to technical limitations, the list of stops is by default focusable. That means that if a user hits the TAB key,
        // the focus will move to the next stop in the list. However we only want to allow to traverse the list by arrows
        // and move out of the list when TAB is pressed. To do this, we temporarily disable focusability of the list when
        // TAB is pressed (and reenable it once the focus is out).
        disableFocus(this.scheduleRefs);
        break;
      default:
        break;
    }
  };

  render() {
    const { schedule, searchMode } = this.props;
    // move this to a selector
    const displaySchedule = generateDisplaySchedule([...schedule], searchMode);
    return (
      <div data-testid="timetables-schedule" className={bem} role="grid" tabIndex={-1} aria-label="Use up and down arrows to navigate">
        {displaySchedule.map((item, index) => {
          this.scheduleRefs.push(React.createRef());
          return (
            <div
              className={item.hourChange ? scheduleClassNameWithHourChange : scheduleClassName}
              role="row"
              key={item.key}
              ref={this.scheduleRefs[index]}
              onKeyDown={this.traverseSchedule}
              onFocus={() => { this.curRefIndex = index; }}
              // once the user moves the focus out of the list, we want to make sure the list can be focusable again
              onBlur={() => enableFocus(this.scheduleRefs)}
              tabIndex={0}
            >
              <span data-testid="timetables-schedule-meridiem" className={`${bem}__col ${bem}__col--am`} role="gridcell">
                {item.am}
              </span>
              <span data-testid="timetables-schedule-firstlast" className={`${bem}__col ${bem}__col--busmarker`} role="gridcell">
                {item.busMarker}
              </span>
              <span className={`${bem}__col ${bem}__col--time`} role="gridcell">
                {item.time}
              </span>
            </div>
          );
        })}
      </div>
    );
  }
}

Schedule.propTypes = {
  schedule: PropTypes.array.isRequired,
  searchMode: PropTypes.string.isRequired,
};
